/**
 * AiInput hook — contenteditable input with inline context pills.
 *
 * Uses a contenteditable div for rich display (pills) and syncs a hidden
 * input with the serialised text for form submission.
 *
 * Pills are non-editable spans with a format like:
 *   @[Hero Section](post:Hero Section)
 *   @[hero-heading in Hero Section](post:Hero Section|element:hero-heading)
 *
 * The hidden input receives the serialised text where pills become their
 * token string, e.g. "@[Hero Section](post:Hero Section)"
 */
// Elements a browser uses to lay out separate lines inside a contenteditable.
// Each one ends a line when the field is serialised back to text.
const BLOCK_TAGS = new Set([
  "DIV",
  "P",
  "LI",
  "UL",
  "OL",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "BLOCKQUOTE",
  "PRE",
  "SECTION",
  "ARTICLE",
  "TR",
]);

export const AiInput = {
  mounted() {
    this.editor = this.el;
    this.hiddenInput = document.getElementById(this.el.dataset.hiddenInputId);

    // Ctrl+Enter / Cmd+Enter to submit
    this.editor.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        this.syncHiddenInput();
        this.editor
          .closest("form")
          .dispatchEvent(new Event("submit", { bubbles: true }));
      }
    });

    // Sync on every input
    this.editor.addEventListener("input", () => {
      this.syncHiddenInput();
    });

    // Paste as plain text. The default paste drops the source document's own
    // markup into the field — styling, tables, anchors — which then has to be
    // serialised back out. Taking `text/plain` keeps what the author sees and
    // what the assistant receives identical, with line breaks preserved.
    this.editor.addEventListener("paste", (e) => {
      const plain = e.clipboardData?.getData("text/plain");
      if (plain === undefined || plain === null) return;

      e.preventDefault();
      this.insertPlainText(plain);
      this.syncHiddenInput();
    });

    // Handle backspace into pill — delete the whole pill
    this.editor.addEventListener("keydown", (e) => {
      if (e.key === "Backspace") {
        const sel = window.getSelection();
        if (!sel.rangeCount) return;

        const range = sel.getRangeAt(0);
        if (!range.collapsed) return;

        const node = range.startContainer;
        const offset = range.startOffset;

        // If cursor is right after a pill span
        if (node.nodeType === Node.TEXT_NODE && offset === 0) {
          const prev = node.previousSibling;
          if (
            prev &&
            prev.classList &&
            prev.classList.contains("ai-ref-pill")
          ) {
            e.preventDefault();
            prev.remove();
            this.syncHiddenInput();
            return;
          }
        }

        // If cursor is at start of editor and previous sibling is pill
        if (node === this.editor && offset > 0) {
          const child = this.editor.childNodes[offset - 1];
          if (
            child &&
            child.classList &&
            child.classList.contains("ai-ref-pill")
          ) {
            e.preventDefault();
            child.remove();
            this.syncHiddenInput();
            return;
          }
        }
      }
    });

    // Listen for server-pushed pill insertions
    this.handleEvent("ai-insert-ref", ({ ref }) => {
      this.insertPill(ref);
    });

    // Listen for server-pushed clear (after send)
    this.handleEvent("ai-clear-input", () => {
      this.editor.innerHTML = "";
      this.syncHiddenInput();
    });

    this.handleEvent("ai-insert-text", ({ text }) => {
      this.insertText(text);
    });
  },

  /**
   * Insert a pill at current cursor position (or end if no focus).
   * ref format: "post:Post Name" or "post:Post Name|element:element-id"
   */
  insertPill(ref) {
    const pill = this.createPillElement(ref);

    // Focus editor if not focused
    if (document.activeElement !== this.editor) {
      this.editor.focus();
      // Move cursor to end
      const range = document.createRange();
      range.selectNodeContents(this.editor);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }

    const sel = window.getSelection();
    if (sel.rangeCount) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(pill);

      // Add a space after the pill and place cursor there
      const space = document.createTextNode("\u00A0");
      pill.after(space);
      range.setStartAfter(space);
      range.setEndAfter(space);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      this.editor.appendChild(pill);
      this.editor.appendChild(document.createTextNode("\u00A0"));
    }

    this.syncHiddenInput();
  },

  insertText(text) {
    const value = `${text} `;

    if (document.activeElement !== this.editor) {
      this.editor.focus();
      const range = document.createRange();
      range.selectNodeContents(this.editor);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }

    const sel = window.getSelection();
    if (sel.rangeCount) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const node = document.createTextNode(value);
      range.insertNode(node);
      range.setStartAfter(node);
      range.setEndAfter(node);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      this.editor.appendChild(document.createTextNode(value));
    }

    this.syncHiddenInput();
  },

  /**
   * Create a pill span element from a ref string.
   */
  createPillElement(ref) {
    const pill = document.createElement("span");
    pill.className = "ai-ref-pill";
    pill.contentEditable = "false";
    pill.dataset.ref = ref;

    // Parse for display
    const parts = ref.split("|");
    const postPart = parts.find((p) => p.startsWith("post:"));
    const elementPart = parts.find((p) => p.startsWith("element:"));

    const postName = postPart ? postPart.substring(5) : "";
    const elementId = elementPart ? elementPart.substring(8) : "";

    // Icon + label
    let label;
    let iconSvg;
    if (elementId) {
      label = `${elementId}`;
      // Element icon (code brackets)
      iconSvg = `<svg class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/></svg>`;
    } else {
      label = postName;
      // Section icon (document)
      iconSvg = `<svg class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>`;
    }

    pill.innerHTML = `${iconSvg}<span class="ai-ref-pill-label">${this.escapeHtml(label)}</span><button type="button" class="ai-ref-pill-remove" tabindex="-1">&times;</button>`;

    // Tooltip showing full ref
    if (elementId) {
      pill.title = `${postName} > ${elementId}`;
    }

    // Remove button handler
    pill.querySelector(".ai-ref-pill-remove").addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      pill.remove();
      // Remove trailing space if present
      const next = pill.nextSibling;
      if (
        next &&
        next.nodeType === Node.TEXT_NODE &&
        next.textContent === "\u00A0"
      ) {
        next.remove();
      }
      this.syncHiddenInput();
    });

    return pill;
  },

  /**
   * Serialise contenteditable contents to text with pill tokens.
   * Pills become @[label](ref) in the serialised output.
   */
  /**
   * Insert text at the caret, one text node per line separated by <br>, so the
   * field shows the same line structure that gets serialised back out.
   */
  insertPlainText(plain) {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) {
      this.editor.appendChild(document.createTextNode(plain));
      return;
    }

    const range = sel.getRangeAt(0);
    range.deleteContents();

    const fragment = document.createDocumentFragment();
    plain.split("\n").forEach((line, index) => {
      if (index > 0) fragment.appendChild(document.createElement("br"));
      if (line !== "") fragment.appendChild(document.createTextNode(line));
    });

    const last = fragment.lastChild;
    range.insertNode(fragment);

    if (last) {
      range.setStartAfter(last);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  },

  syncHiddenInput() {
    let text = "";
    this.editor.childNodes.forEach((node) => {
      text = this.appendNode(text, node);
    });

    this.hiddenInput.value = text
      .replace(/\u00A0/g, " ")
      // A block that ends a line and another that starts one would otherwise
      // stack up blank lines the author never typed.
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  },

  /**
   * Serialise one node, preserving line structure.
   *
   * Browsers wrap each line of a multi-line paste in its own block element, so
   * walking only the top level and reading `textContent` fuses every line into
   * one. That matters beyond looks: the text goes to the assistant, and
   * markdown is defined by its newlines \u2014 headings, lists and paragraph breaks
   * all disappear when they are stripped.
   */
  serialiseNode(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return "";

    if (node.classList && node.classList.contains("ai-ref-pill")) {
      const ref = node.dataset.ref;
      const label =
        node.querySelector(".ai-ref-pill-label")?.textContent || ref;
      return `@[${label}](${ref})`;
    }

    if (node.tagName === "BR") return "\n";

    let inner = "";
    node.childNodes.forEach((child) => {
      inner = this.appendNode(inner, child);
    });

    return BLOCK_TAGS.has(node.tagName) ? `${inner}\n` : inner;
  },

  /**
   * Append a node's text, opening a new line when a block follows content that
   * did not end one. The first line of a contenteditable is usually a bare text
   * node with the rest in blocks, so without this the first two lines fuse.
   */
  appendNode(text, node) {
    const piece = this.serialiseNode(node);
    if (piece === "") return text;

    const startsLine =
      node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(node.tagName);

    if (startsLine && text !== "" && !text.endsWith("\n")) {
      return `${text}\n${piece}`;
    }

    return text + piece;
  },

  escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  },
};
